import { Fill, Stroke } from "ol/style";
import { Color, asString } from "ol/color";
import { ColorLike, asColorLike } from "ol/colorlike";
import { Size } from "ol/size";
import { FontSymbolOptions } from "../model";

export class FontIconCanvas {
    readonly  WORKER_OFFSCREEN_CANVAS = typeof OffscreenCanvas !== 'undefined'
    readonly defaultStrokeStyle = "";
    readonly defaultLineWidth = 1;

    form: string;
    anchor_: number[];
    canvas_: { [key: number]: HTMLCanvasElement | OffscreenCanvas };
    fill_: Fill | null;
    stroke_: Stroke | null;
    origin_: number[];
    points_: number;
    radius_: number;
    radius2_: number | undefined;
    angle_: number;

    size_: Size | null;
    imageSize_: Size | null;
    hitDetectionImageSize_: Size | null;

    hitDetectionCanvas_: HTMLCanvasElement | HTMLVideoElement | HTMLImageElement | null | OffscreenCanvas;

    fontIcon_: string | undefined;
    fontStyle_: string;
    fontSize_: number;

    color_: string;
    gradient_: boolean;
    canvas: any;
    context: any;
    size: number;
    strokeStyle: Color | ColorLike | string = "";
    strokeWidth = 0;

    constructor(options: FontSymbolOptions, canvas?: any) {
        this.gradient_ = options.gradient !== undefined ? options.gradient : false;
        this.color_ = options.color !== undefined ? options.color : "#000";
        this.form = options.form || "none";
        this.canvas_ = {};
        this.hitDetectionCanvas_ = null;
        this.fill_ = options.fill !== undefined ? options.fill : null;
        this.origin_ = [0, 0];
        this.points_ = options.points || 0;
        this.radius_ = options.radius !== undefined ? options.radius : options.radius;
        this.radius2_ = options.radius2;
        this.angle_ = options.angle !== undefined ? options.angle : 0;
        this.stroke_ = options.stroke !== undefined ? options.stroke : null;

        this.anchor_ = [0, 0];
        this.size_ = null;
        this.imageSize_ = null;
        this.hitDetectionImageSize_ = null;

        this.fontIcon_ = options.symbolChar;
        this.fontStyle_ = '';
        this.fontSize_ = options.fontSize !== undefined ? options.fontSize : 1;

        if (this.stroke_) {
            this.strokeStyle = this.stroke_.getColor() || this.defaultStrokeStyle;

            this.strokeStyle = asColorLike(this.strokeStyle);
            this.strokeWidth = this.stroke_.getWidth() || this.defaultLineWidth;
        }

        this.size = 2 * (this.radius_ + this.strokeWidth) + 1;
        this.canvas = canvas || document.createElement('canvas');
        this.canvas.width = this.size;
        this.canvas.height = this.size;
        this.context = this.canvas.getContext('2d');
    }

    buildCanvas() {
        const fcolor = this.fill_ ? this.fill_.getColor() : "#000";
        const scolor = this.stroke_ ? this.stroke_.getColor() : "#000";
        const tr= this.drawPath(this.context);

        if (this.fill_) {
            if (this.gradient_ && this.form != "none") {
                const grd = this.context.createLinearGradient(0, 0, this.size / 2, this.size);
                grd.addColorStop(1, asString(fcolor.toString()));
                grd.addColorStop(0, asString(scolor.toString()));
                this.context.fillStyle = grd;
            } else {
                this.context.fillStyle = asString(fcolor.toString());
            }
            this.context.fill();
        }
        if (this.stroke_ && this.strokeWidth) {
            this.context.strokeStyle = this.strokeStyle;
            this.context.lineWidth = this.strokeWidth;
            this.context.stroke();
        }

        //draw the symbol
        if (this.fontIcon_) {

            const sizeFont = (2 * tr.fac * (this.radius_) * this.fontSize_);
            this.context.font = this.fontStyle_ + ' ' + sizeFont + "px " + "abc-icons";
            this.context.strokeStyle = this.context.fillStyle;
            this.context.lineWidth = this.strokeWidth * (this.form == "none" ? 2 : 1);
            this.context.fillStyle = asString(this.color_ || scolor.toString());
            this.context.textAlign = "center";
            this.context.textBaseline = "middle";
            const t = this.fontIcon_;
            if (this.strokeWidth && scolor != "transparent") {
                this.context.strokeText(t, tr.posX, tr.posY);
            }
            this.context.fillText(t, tr.posX, tr.posY);
        }
    }

    private drawPath(context: any) {
        const s = 2 * this.radius_ + this.strokeWidth + 1;
        const w = this.strokeWidth / 2;
        const c = this.size / 2;

        let transfo = { fac: 1, posX: this.size / 2, posY: this.size / 2 };
        context.lineJoin = 'round';
        context.beginPath();
        this.drawPolygon(context);

        switch (this.form) {
            case "none": {
                transfo.fac = 1;
                break;
            }
            case "circle":
            case "ban": {
                context.arc(c, c, s / 2, 0, 2 * Math.PI, true);
                break;
            }
            case "poi": {
                context.arc(c, c - 0.4 * this.radius_, 0.6 * this.radius_, 0.15 * Math.PI, 0.85 * Math.PI, true);
                context.lineTo(c - 0.89 * 0.05 * s, (0.95 + 0.45 * 0.05) * s + w);
                context.arc(c, 0.95 * s + w, 0.05 * s, 0.85 * Math.PI, 0.15 * Math.PI, true);
                transfo = { fac: 0.45, posX: c, posY: c - 0.35 * this.radius_ };
                break;
            }
            case "bubble": {
                context.arc(c, c - 0.2 * this.radius_, 0.8 * this.radius_, 0.4 * Math.PI, 0.6 * Math.PI, true);
                context.lineTo(0.5 * s + w, s + w);
                transfo = { fac: 0.7, posX: c, posY: c - 0.2 * this.radius_ };
                break;
            }
            case "marker": {
                context.arc(c, c - 0.2 * this.radius_, 0.8 * this.radius_, 0.25 * Math.PI, 0.75 * Math.PI, true);
                context.lineTo(0.5 * s + w, s + w);
                transfo = { fac: 0.7, posX: c, posY: c - 0.2 * this.radius_ };
                break;
            }
            case "coma": {
                context.moveTo(c + 0.8 * this.radius_, c - 0.2 * this.radius_);
                context.quadraticCurveTo(0.95 * s + w, 0.75 * s + w, 0.5 * s + w, s + w);
                context.arc(c, c - 0.2 * this.radius_, 0.8 * this.radius_, 0.45 * Math.PI, 0, false);
                transfo = { fac: 0.7, posX: c, posY: c - 0.2 * this.radius_ };
                break;
            }
            default: {
                let pts;
                switch (this.form) {
                    case "shield": {
                        pts = [0.05, 0, 0.95, 0, 0.95, 0.8, 0.5, 1, 0.05, 0.8, 0.05, 0];
                        transfo.posY = 0.45 * s + w;
                        break;
                    }
                    case "blazon": {
                        pts = [0.1, 0, 0.9, 0, 0.9, 0.8, 0.6, 0.8, 0.5, 1, 0.4, 0.8, 0.1, 0.8, 0.1, 0];
                        transfo.fac = 0.8;
                        transfo.posY = 0.4 * s + w;
                        break;
                    }
                    case "bookmark": {
                        pts = [0.05, 0, 0.95, 0, 0.95, 1, 0.5, 0.8, 0.05, 1, 0.05, 0];
                        transfo.fac = 0.9;
                        transfo.posY = 0.4 * s + w;
                        break;
                    }
                    case "hexagon": {
                        pts = [0.05, 0.2, 0.5, 0, 0.95, 0.2, 0.95, 0.8, 0.5, 1, 0.05, 0.8, 0.05, 0.2];
                        transfo.fac = 0.9;
                        transfo.posY = 0.5 * s + w;
                        break;
                    }
                    case "diamond": {
                        pts = [0.25, 0, 0.75, 0, 1, 0.2, 1, 0.4, 0.5, 1, 0, 0.4, 0, 0.2, 0.25, 0];
                        transfo.fac = 0.75;
                        transfo.posY = 0.35 * s + w;
                        break;
                    }
                    case "triangle": {
                        pts = [0, 0, 1, 0, 0.5, 1, 0, 0];
                        transfo.fac = 0.6;
                        transfo.posY = 0.3 * s + w;
                        break;
                    }
                    case "sign": {
                        pts = [0.5, 0.05, 1, 0.95, 0, 0.95, 0.5, 0.05];
                        transfo.fac = 0.7;
                        transfo.posY = 0.65 * s + w;
                        break;
                    }
                    case "lozenge": {
                        pts = [0.5, 0, 1, 0.5, 0.5, 1, 0, 0.5, 0.5, 0];
                        transfo.fac = 0.7;
                        break;
                    }
                    case "square":
                    default: {
                        pts = [0, 0, 1, 0, 1, 1, 0, 1, 0, 0];
                        break;
                    }
                }
                for (let i = 0; i < pts.length; i += 2) context.lineTo(pts[i] * s + w, pts[i + 1] * s + w);
            }
        }
        context.closePath();
        return transfo;
    }

    drawPolygon(context: any) {
        let i, angle0, radiusC;

        let points = this.points_;
        if (points === Infinity) {
            context.arc(
                this.size / 2,
                this.size / 2,
                this.radius_,
                0,
                2 * Math.PI,
                true
            );
        } else {
            const radius2 =
                this.radius2_ !== undefined ? this.radius2_ : this.radius_;
            if (radius2 !== this.radius_) {
                points = 2 * points;
            }
            for (i = 0; i <= points; i++) {
                angle0 = (i * 2 * Math.PI) / points - Math.PI / 2 + this.angle_;
                radiusC = i % 2 === 0 ? this.radius_ : radius2;
                context.lineTo(
                    this.size / 2 + radiusC * Math.cos(angle0),
                    this.size / 2 + radiusC * Math.sin(angle0)
                );
            }
        }
    }
}
